import React, { useState, useRef, useEffect } from 'react';
import { MoreHorizontal, GripVertical, PieChartIcon, BarChartHorizontal, TrendingUp, AreaChart, BarChart3, TableIcon } from './icons';

const chartTypeDetails = {
    PieChart: { label: 'Pie Chart', icon: <PieChartIcon className="w-4 h-4 mr-2" /> },
    BarChart: { label: 'Bar Chart', icon: <BarChartHorizontal className="w-4 h-4 mr-2" /> },
    LineChart: { label: 'Line Chart', icon: <TrendingUp className="w-4 h-4 mr-2" /> },
    AreaChart: { label: 'Area Chart', icon: <AreaChart className="w-4 h-4 mr-2" /> },
    ComboChart: { label: 'Combo Chart', icon: <BarChart3 className="w-4 h-4 mr-2" /> },
};


const ChartCard = ({ 
  title, children, className = '', style, chartKey, currentType, availableTypes, onTypeChange
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [view, setView] = useState('chart');

  useEffect(() => {
      const handleClickOutside = (event) => {
          if (menuRef.current && !menuRef.current.contains(event.target)) {
              setIsMenuOpen(false);
          }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
          document.removeEventListener('mousedown', handleClickOutside);
      };
  }, []);

  const handleTypeSelect = (newType) => {
    onTypeChange(chartKey, newType);
    setIsMenuOpen(false);
  };
  
  const chartContent = React.isValidElement(children)
    ? React.cloneElement(children, { view })
    : children;

  return (
    <div className={`bg-white rounded-xl shadow-sm hover:shadow-lg transition-shadow duration-300 flex flex-col h-full ${className}`} style={style}>
      <div className="flex items-center justify-between p-4 sm:px-6 sm:pt-6 sm:pb-4 border-b border-slate-200">
        <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
        <div className="flex items-center space-x-2">
            <div className="flex items-center p-0.5 bg-slate-100 rounded-md">
                <button
                    className={`p-1 rounded-md transition-colors text-slate-500 ${
                        view === 'chart'
                            ? 'bg-white shadow-sm text-blue-600'
                            : 'hover:bg-slate-200'
                    }`}
                    onClick={() => setView('chart')}
                    aria-pressed={view === 'chart'}
                    aria-label="Switch to chart view"
                    title="Switch to chart view"
                >
                    <BarChart3 className="w-5 h-5" />
                </button>
                <button
                    className={`p-1 rounded-md transition-colors text-slate-500 ${
                        view === 'table'
                            ? 'bg-white shadow-sm text-blue-600'
                            : 'hover:bg-slate-200'
                    }`}
                    onClick={() => setView('table')}
                    aria-pressed={view === 'table'}
                    aria-label="Switch to table view"
                    title="Switch to table view"
                >
                    <TableIcon className="w-5 h-5" />
                </button>
            </div>
            <div className="relative" ref={menuRef}>
                <button 
                    className="text-slate-400 hover:text-slate-600 p-1 rounded-md hover:bg-slate-100" 
                    title="Change chart type"
                    aria-label="Change chart type"
                    onClick={() => setIsMenuOpen(prev => !prev)}
                >
                    <MoreHorizontal className="w-5 h-5" />
                </button>
                {isMenuOpen && (
                    <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
                        <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                            <p className="px-4 py-2 text-xs font-semibold text-slate-400 uppercase">Chart Type</p>
                            {availableTypes.map(type => (
                                <button
                                    key={type}
                                    onClick={() => handleTypeSelect(type)}
                                    className={`w-full text-left flex items-center px-4 py-2 text-sm ${
                                        currentType === type
                                            ? 'bg-blue-50 text-blue-600'
                                            : 'text-slate-700 hover:bg-slate-100'
                                    }`}
                                    role="menuitem"
                                >
                                    {chartTypeDetails[type].icon}
                                    {chartTypeDetails[type].label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <div 
              className="drag-handle cursor-move text-slate-400 hover:text-slate-600 p-1" 
              title="Drag to reorder" 
              aria-label="Drag to reorder card"
            >
                <GripVertical className="w-5 h-5" />
            </div>
        </div>
      </div>
      <div 
        className="flex-grow p-2 sm:p-4 w-full min-h-0"
        role="region"
        aria-label={`${title} - ${currentType} - ${view} view`}
      >
        {chartContent}
      </div>
    </div>
  );
};

export default ChartCard;